import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useGetCricketDetailQuery, useFreezeEventMutation } from '../services/sports/sportsApi';
import Loading from "../components/Loading";

// Menu Down Icon
const MenuDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M7 10l5 5 5-5z" />
  </svg>
);

// Dropdown Menu Component
const DropdownMenu = ({ match, position, onClose }) => {
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const rawMatchName = match.matchName;
  const matchName =
    rawMatchName && rawMatchName !== "undefined" && rawMatchName !== "null"
      ? rawMatchName
      : match.marketName || match.eventName || match.name || String(match.eventId || match.matchId || "Unknown");

  useEffect(
    function () {
      const handleClickOutside = function (event) {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
          onClose();
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return function () {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    },
    [onClose],
  );

  const menuItems = [
    {
      label: "Match Session Plus Minus",
      path: `/sports-betting/match-session-plus-minus/${match.eventId || match.matchId}/${encodeURIComponent(matchName)}`,
    },
    {
      label: "Match Bets",
      path: `/sports-betting/match-bets/${match.eventId || match.matchId}/${encodeURIComponent(matchName)}`,
    },
    {
      label: "Toss Bets",
      path: `/sports-betting/toss-bets/${match.eventId || match.matchId}/${encodeURIComponent(matchName)}`,
    },
    {
      label: "Session Bets",
      path: `/sports-betting/fancy-bets/${match.eventId || match.matchId}/${encodeURIComponent(matchName)}`,
    },
    {
      label: "Completed Session",
      path: `/sports-betting/fancy-profit-loss/${match.eventId || match.matchId}/${encodeURIComponent(matchName)}`,
    },
    {
      label: "Rejected Bets",
      path: `/sports-betting/rejected-bets/${match.eventId || match.matchId}/${encodeURIComponent(matchName)}`,
    },
    {
      label: "Company Report",
      path: `/sports-betting/company-report/${match.eventId || match.matchId}/${encodeURIComponent(matchName)}`,
    },
  ];

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-white border border-gray-300 shadow-lg animate-slideDown"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        minWidth: "200px",
      }}
    >
      {menuItems.map(function (item, index) {
        return (
          <div
            key={index}
            onClick={function () {
              navigate(item.path);
              onClose();
            }}
            className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-200 last:border-b-0"
          >
            {item.label}
          </div>
        );
      })}
    </div>
  );
};

const CompletedMatchesPage = function () {
  const navigate = useNavigate();

  const isAdmin = (() => {
    try {
      const userData = localStorage.getItem('user_data');
      if (userData) return JSON.parse(userData).userType === 'Admin';
    } catch (_) {}
    return false;
  })();

  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 50;

  const { data: cricketData, isLoading: cricketLoading, error: cricketError } = useGetCricketDetailQuery({
    filter: 'completed',
    page: currentPage,
    limit: PAGE_SIZE,
  });

  const matches = cricketData?.data || [];
  const pagination = {
    total: cricketData?.pagination?.total || 0,
    totalPages: cricketData?.pagination?.totalPages || 1,
  };
  const loading = cricketLoading;
  const error = cricketError ? (cricketError.message || "Failed to load completed matches") : null;

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, match: null });

  const [freezeEvent, { isLoading: freezeLoading }] = useFreezeEventMutation();

  const handleDropdownClick = function (e, matchId) {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom,
      left: rect.left,
    });
    setActiveDropdown(activeDropdown === matchId ? null : matchId);
  };

  const handleCloseDropdown = function () {
    setActiveDropdown(null);
  };

  const handleFreezeConfirm = async () => {
    const match = confirmModal.match;
    if (!match) return;
    const eventId = match.eventId || match.matchId;
    try {
      const result = await freezeEvent(eventId).unwrap();
      alert(result?.message || 'Ledger created successfully!');
    } catch (err) {
      alert(err?.data?.message || err?.message || 'Something went wrong');
    } finally {
      setConfirmModal({ isOpen: false, match: null });
    }
  };

  const activeDropdownMatch = matches.find(function (m) {
    return (m.matchId === activeDropdown || m.eventId === activeDropdown);
  });

  return (
    <div className="w-full animate-fadeIn">
      <div className="flex justify-center">
        <div className="w-full px-2">
          <div className="bg-white rounded shadow border border-gray-200 animate-fadeInUp">
            {/* Header */}
            <div className="bg-gray-600 px-5 py-2 flex items-center justify-between">
              <span className="text-xl text-white font-normal capitalize animate-slideInLeft">
                Completed Matches
              </span>
              <button
                onClick={function () {
                  navigate(-1);
                }}
                className="bg-blue-500 text-white px-4 py-1 hover:bg-blue-600 transition-colors duration-200"
                style={{ borderRadius: 0 }}
              >
                Back
              </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex justify-center py-8 animate-fadeIn">
                  <Loading />
                </div>
              ) : error ? (
                <div className="p-4 text-red-500 text-center animate-fadeIn">{error}</div>
              ) : (
                <>
                  <table className="w-full border-collapse text-sm border border-gray-300">
                    <thead>
                      <tr className="bg-gray-100 animate-slideInLeft">
                        <th className="p-2 border border-gray-300 text-left font-medium"></th>
                        <th className="p-2 border border-gray-300 text-left font-medium">
                          Market Name
                        </th>
                        <th className="p-2 border border-gray-300 text-left font-medium">
                          Time
                        </th>
                        <th className="p-2 border border-gray-300 text-left font-medium">
                          Won by
                        </th>
                        <th className="p-2 border border-gray-300 text-left font-medium">
                          Net P/L
                        </th>
                        {isAdmin && (
                          <th className="p-2 border border-gray-300 text-left font-medium">
                            Action
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {matches.length === 0 ? (
                        <tr>
                          <td
                            colSpan="5"
                            className="p-8 text-center text-gray-500 bg-gray-50 animate-fadeIn"
                          >
                            No completed matches found
                          </td>
                        </tr>
                      ) : (
                        matches.map(function (match, index) {
                          const rowBgClass = index % 2 === 0 ? "" : "bg-gray-50";
                          const matchName = match.marketName || match.matchName || match.eventName || match.name || "-";
                          const rowDelay = `${Math.min(index * 0.04, 0.6)}s`;

                          return (
                            <tr
                              key={match.eventId || match.matchId}
                              className={rowBgClass + " hover:bg-blue-50 transition-colors duration-150 animate-row-in"}
                              style={{ animationDelay: rowDelay }}
                            >
                              <td className="p-2 border border-gray-300">
                                <span
                                  onClick={function (e) {
                                    handleDropdownClick(e, match.eventId || match.matchId);
                                  }}
                                  className="bg-blue-500 text-white cursor-pointer inline-flex items-center justify-center hover:bg-blue-600 transition-colors duration-150"
                                  style={{ padding: "3px 3px" }}
                                >
                                  <MenuDownIcon />
                                </span>
                              </td>
                              <td className="p-2 border border-gray-300">
                                {matchName}
                              </td>
                              <td className="p-2 border border-gray-300">
                                <span className="">
                                  {match.startTime ? new Date(match.startTime).toLocaleString('en-GB', {
                                    timeZone: 'UTC',
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  }).replace(',', '') : '-'}
                                </span>
                              </td>
                              <td className="p-2 border border-gray-300">
                                {match.wonBy || "-"}
                              </td>
                              <td
                                className={`p-2 border border-gray-300 ${match.profitLoss >= 0 ? 'text-green-600' : 'text-red-600'} font-semibold animate-countUp`}
                                style={{ animationDelay: rowDelay }}
                              >
                                {match.profitLoss !== undefined ? match.profitLoss : '-'}
                              </td>
                              {isAdmin && (
                                <td className="p-2 border border-gray-300">
                                  <button
                                    onClick={() => setConfirmModal({ isOpen: true, match })}
                                    className="bg-green-600 text-white px-3 py-1 text-xs hover:bg-green-700"
                                  >
                                    Create Ledger
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-300 bg-white animate-fadeIn" style={{ animationDelay: '0.3s' }}>
                      <span className="text-sm text-gray-600">
                        Total {pagination.total} records
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                          className="px-2 py-1 text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          «
                        </button>
                        <button
                          onClick={() => setCurrentPage(p => p - 1)}
                          disabled={currentPage === 1}
                          className="px-2 py-1 text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          ‹
                        </button>
                        {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                          .filter(p => p === 1 || p === pagination.totalPages || Math.abs(p - currentPage) <= 2)
                          .reduce((acc, p, idx, arr) => {
                            if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                            acc.push(p);
                            return acc;
                          }, [])
                          .map((item, idx) =>
                            item === '...' ? (
                              <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-400">…</span>
                            ) : (
                              <button
                                key={item}
                                onClick={() => setCurrentPage(item)}
                                className={`px-3 py-1 text-sm border ${currentPage === item ? 'bg-blue-500 text-white border-blue-500' : 'border-gray-300 hover:bg-gray-100'}`}
                              >
                                {item}
                              </button>
                            )
                          )}
                        <button
                          onClick={() => setCurrentPage(p => p + 1)}
                          disabled={currentPage === pagination.totalPages}
                          className="px-2 py-1 text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          ›
                        </button>
                        <button
                          onClick={() => setCurrentPage(pagination.totalPages)}
                          disabled={currentPage === pagination.totalPages}
                          className="px-2 py-1 text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          »
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dropdown Menu */}
      {activeDropdown && activeDropdownMatch && (
        <DropdownMenu
          match={activeDropdownMatch}
          position={dropdownPosition}
          onClose={handleCloseDropdown}
        />
      )}

      {/* Confirmation Modal */}
      {confirmModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', backgroundColor: 'rgba(255,255,255,0.15)' }}>
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold mb-2">Create Ledger</h3>
            <p className="text-sm text-gray-600 mb-5">
              Kya aap <strong>{confirmModal.match?.marketName || confirmModal.match?.matchName || 'is match'}</strong> ka ledger create karna chahte hain?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmModal({ isOpen: false, match: null })}
                className="px-4 py-2 text-sm border border-gray-300 hover:bg-gray-100"
                disabled={freezeLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleFreezeConfirm}
                className="px-4 py-2 text-sm bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                disabled={freezeLoading}
              >
                {freezeLoading ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompletedMatchesPage;
